import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Lead } from '../types/Lead';
import { COLLECTION_NAMES } from '../utils/constants';

export const firestoreService = {
  // Generic document operations
  async createDocument<T>(collectionName: string, data: T): Promise<{ id: string | null; error: string | null }> {
    try {
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return { id: docRef.id, error: null };
    } catch (error: any) {
      return { id: null, error: error.message };
    }
  },

  async updateDocument<T>(collectionName: string, id: string, data: Partial<T>): Promise<{ error: string | null }> {
    try {
      const docRef = doc(db, collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  },

  async deleteDocument(collectionName: string, id: string): Promise<{ error: string | null }> {
    try {
      await deleteDoc(doc(db, collectionName, id));
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  },

  async getDocument<T>(collectionName: string, id: string): Promise<{ data: T | null; error: string | null }> {
    try {
      const docRef = doc(db, collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { data: { id: docSnap.id, ...docSnap.data() } as T, error: null };
      } else {
        return { data: null, error: 'Document not found' };
      }
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  },

  async getDocuments<T>(
    collectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<{ data: T[]; error: string | null }> {
    try {
      const q = query(collection(db, collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      const data: T[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as T);
      });

      return { data, error: null };
    } catch (error: any) {
      return { data: [], error: error.message };
    }
  },

  // Real-time listeners
  subscribeToCollection<T>(
    collectionName: string,
    callback: (data: T[]) => void,
    constraints: QueryConstraint[] = []
  ) {
    const q = query(collection(db, collectionName), ...constraints);

    return onSnapshot(q, (querySnapshot) => {
      const data: T[] = [];
      querySnapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() } as T;
        data.push(docData);
      });

      callback(data);
    }, (error) => {
      console.error(`❌ FirestoreService: Error subscribing to collection ${collectionName}:`, error);
    });
  },

  subscribeToDocument<T>(
    collectionName: string,
    id: string,
    callback: (data: T | null) => void
  ) {
    const docRef = doc(db, collectionName, id);

    return onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        callback({ id: docSnap.id, ...docSnap.data() } as T);
      } else {
        callback(null);
      }
    });
  },

  // Lead-specific operations
  leads: {
    async create(lead: Omit<Lead, 'id'>): Promise<{ id: string | null; error: string | null }> {
      return firestoreService.createDocument(COLLECTION_NAMES.LEADS, lead);
    },

    async update(id: string, data: Partial<Lead>): Promise<{ error: string | null }> {
      return firestoreService.updateDocument(COLLECTION_NAMES.LEADS, id, data);
    },

    async delete(id: string): Promise<{ error: string | null }> {
      return firestoreService.deleteDocument(COLLECTION_NAMES.LEADS, id);
    },

    async getById(id: string): Promise<{ data: Lead | null; error: string | null }> {
      return firestoreService.getDocument<Lead>(COLLECTION_NAMES.LEADS, id);
    },

    async getAll(): Promise<{ data: Lead[]; error: string | null }> {
      return firestoreService.getDocuments<Lead>(COLLECTION_NAMES.LEADS, [
        orderBy('processed.averageScore', 'desc')
      ]);
    },

    async getByScore(minScore: number): Promise<{ data: Lead[]; error: string | null }> {
      return firestoreService.getDocuments<Lead>(COLLECTION_NAMES.LEADS, [
        where('processed.averageScore', '>=', minScore),
        orderBy('processed.averageScore', 'desc')
      ]);
    },

    async getBySector(sector: string): Promise<{ data: Lead[]; error: string | null }> {
      return firestoreService.getDocuments<Lead>(COLLECTION_NAMES.LEADS, [
        where('metadata.sector', '==', sector),
        orderBy('processed.averageScore', 'desc')
      ]);
    },

    subscribeToAll(callback: (leads: Lead[]) => void) {
      return firestoreService.subscribeToCollection<Lead>(COLLECTION_NAMES.LEADS, callback, [
        orderBy('processed.averageScore', 'desc')
      ]);
    }
  },

  // User preferences and settings
  userSettings: {
    async save(userId: string, settings: any): Promise<{ error: string | null }> {
      return firestoreService.updateDocument('userSettings', userId, settings);
    },

    async get(userId: string): Promise<{ data: any | null; error: string | null }> {
      return firestoreService.getDocument('userSettings', userId);
    }
  }
};